// ###### IMPORTS
import React, { useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import ClusterForm from './ClusterForm';
import SpinnerLoad from '../../moduleComponents/spinner_load';
import ConfirmModal from '../../moduleComponents/ConfirmModal';

// ###### COMPONENT
const CardClusters = ({ clusters = [], isLoading = false, onCreateCluster, onUpdateCluster, onDeleteCluster }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCluster, setEditingCluster] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clusterToDelete, setClusterToDelete] = useState(null);

  // ###### HANDLERS
  const handleCreateClick = () => {
    setEditingCluster(null);
    setShowForm(true);
  };

  const handleEditClick = (cluster) => {
    setEditingCluster(cluster);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCluster(null);
  };

  const handleSave = async (clusterData) => {
    setIsSubmitting(true);
    try {
      if (editingCluster) {
        await onUpdateCluster(editingCluster.id, clusterData);
      } else {
        await onCreateCluster(clusterData);
      }
      setShowForm(false);
      setEditingCluster(null);
    } catch (error) {
      // Error already handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (cluster) => {
    setClusterToDelete(cluster);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!clusterToDelete) return;
    setIsSubmitting(true);
    try {
      await onDeleteCluster(clusterToDelete.id);
      setShowDeleteModal(false);
      setClusterToDelete(null);
    } catch (error) {
      // Error already handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setClusterToDelete(null);
  };

  // ###### RENDER
  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Clusters</h3>
        {!showForm && (
          <button
            onClick={handleCreateClick}
            className="p-1.5 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-4 pb-4 border-b border-gray-200">
          <ClusterForm
            cluster={editingCluster}
            onSave={handleSave}
            onCancel={handleCancelForm}
            isSubmitting={isSubmitting}
          />
        </div>
      )}

      {/* Clusters List */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <SpinnerLoad size="small" />
        </div>
      ) : clusters.length === 0 ? (
        <div className="text-sm text-gray-500 py-4 text-center">
          {showForm ? 'Create your first cluster' : 'No clusters yet. Create one to organize your notes!'}
        </div>
      ) : (
        <div className="space-y-2">
          {clusters.map((cluster) => (
            <div
              key={cluster.id}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cluster.color }}
                />
                <span className="text-sm text-gray-800 truncate">{cluster.name}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleEditClick(cluster)}
                  className="p-1.5 rounded hover:bg-gray-200 transition-colors text-gray-600"
                  disabled={isSubmitting}
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteClick(cluster)}
                  className="p-1.5 rounded hover:bg-red-100 transition-colors text-red-600"
                  disabled={isSubmitting}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Cluster"
        message={`Are you sure you want to delete "${clusterToDelete?.name || 'this cluster'}"? Notes associated with this cluster will not be deleted, but will no longer be grouped.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white shadow"
      />
    </div>
  );
};

export default CardClusters;
